import type { DashboardFilterDefinitionItem } from '@/types/DashboardDomainRegistry.types'
import type { DiscoveredFilterValue } from '@/types/DashboardFilterDiscovery.types'
import {
  DefaultBadge,
  DiscoveryFilterRow,
  GroupedCheckboxList,
  PillToggle,
  SinglePillToggle,
} from './FiltersBlock.primitives'

/**
 * FilterRow — generic UI hint dispatcher.
 *
 * Slice 4 of EPIC-132's metadata refactor (TDD §7.5) collapses the four
 * `hasDomain('X')` branches in FiltersBlock into one generic dispatcher that
 * reads each filter's `uiHint` field from the SDK-owned domain definition and
 * routes to the appropriate primitive — preserving the exact mockup-faithful
 * chrome owned by FiltersBlock.primitives.
 *
 * Discovery vs static branching:
 *  - `filter.isDiscoveryDriven === true` → wraps the primitive in
 *    DiscoveryFilterRow, which handles loading / error / credential-expired /
 *    zero-seasons states.
 *  - Otherwise renders the primitive directly with `staticChoices`, filtered
 *    through `displayChoices` whitelist if present.
 */

export interface FilterRowProps {
  filter: DashboardFilterDefinitionItem
  /** Qualified dashboard-era id used by discovery dispatch. */
  qualifiedDomainId: string | null
  /** Unqualified picker key — used only for nested DiscoveryFilterRow Redux state. */
  domainKey: string
  selectedValues: string[]
  onToggle: (value: string) => void
  /** Required for single-cardinality filters. */
  onSetSingle?: (value: string) => void
  /** Bubbles up to FiltersBlock so the Confirm CTA hides during zero-seasons. */
  onBlockingChange?: (blocking: boolean) => void
  /**
   * For discovery-driven filters with parent context (e.g. session needs
   * season). The caller injects the closure when firing discovery; we pass
   * separate retry/checkAgain handlers down to keep the dispatch logic local
   * to the parent.
   */
  onRetry: () => void
  onCheckAgain: () => void
  /** True when the choice array exactly matches the strategy default. */
  showDefaultBadge?: boolean
  /** Override for the filter row group's aria-label. */
  ariaLabelOverride?: string
}

// ─── FilterRowHeader ──────────────────────────────────────────────────────────

function FilterRowHeader({
  filter,
  showDefaultBadge,
}: {
  filter: DashboardFilterDefinitionItem
  showDefaultBadge?: boolean
}) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm font-semibold text-primary">
        {filter.displayName}
      </span>
      {showDefaultBadge && <DefaultBadge label="Default" />}
    </div>
  )
}

// ─── FallbackFilterRow ────────────────────────────────────────────────────────

/**
 * Rendered when `uiHint` is absent or unknown. Per TDD §7.10 backward-compat,
 * the FE renders an info note rather than a blank card so unknown filters are
 * visible in development without breaking production rendering.
 */
function FallbackFilterRow({
  filter,
}: {
  filter: DashboardFilterDefinitionItem
}) {
  return (
    <div className="mb-4">
      <FilterRowHeader filter={filter} />
      <p className="text-xs text-secondary italic px-2.5 py-1.5 bg-muted rounded-md border border-border">
        Filter type not yet supported in this client.
      </p>
    </div>
  )
}

// ─── Hint dispatcher ──────────────────────────────────────────────────────────

function renderHint(
  filter: DashboardFilterDefinitionItem,
  choices: DiscoveredFilterValue[],
  selectedValues: string[],
  onToggle: (value: string) => void,
  onSetSingle: ((value: string) => void) | undefined,
  showDefaultBadge: boolean | undefined,
  ariaLabelOverride: string | undefined,
): JSX.Element {
  const groupAriaLabel =
    ariaLabelOverride ??
    `Select ${filter.displayName.toLowerCase()}${
      filter.cardinality === 'multi' ? ' (multi-select)' : ''
    }`

  switch (filter.uiHint) {
    case 'pills-multiselect':
    case 'pills-multiselect-static':
      return (
        <div className="mb-4">
          <FilterRowHeader filter={filter} showDefaultBadge={showDefaultBadge} />
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label={groupAriaLabel}
          >
            {choices.map(c => (
              <PillToggle
                key={c.value}
                label={c.displayName}
                selected={selectedValues.includes(c.value)}
                isCurrent={c.isCurrent}
                size={filter.code === 'season' ? 'season' : 'status'}
                onToggle={() => onToggle(c.value)}
                ariaLabelOverride={
                  filter.code === 'season'
                    ? `Season ${c.displayName}${
                        selectedValues.includes(c.value) ? ' — selected' : ''
                      }${c.isCurrent ? ', current season' : ''}`
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )

    case 'single-select-presets':
      return (
        <div className="mb-4">
          <FilterRowHeader filter={filter} showDefaultBadge={showDefaultBadge} />
          <div
            className="flex flex-wrap gap-1.5"
            role="group"
            aria-label={`Select ${filter.displayName.toLowerCase()} (single-select)`}
          >
            {choices.map(c => (
              <SinglePillToggle
                key={c.value}
                label={c.displayName}
                selected={selectedValues[0] === c.value}
                onSelect={() => onSetSingle?.(c.value)}
              />
            ))}
          </div>
        </div>
      )

    case 'grouped-checkbox-list':
      return (
        <div className="mb-4">
          <FilterRowHeader filter={filter} showDefaultBadge={showDefaultBadge} />
          {choices.length === 0 ? (
            <p className="text-xs text-secondary italic px-2.5 py-1.5 bg-muted rounded-md border border-border">
              No options found.
            </p>
          ) : (
            <GroupedCheckboxList
              ariaLabel={`Select ${filter.displayName.toLowerCase()} (multi-select, optional). Leave empty to include all.`}
              helperNote={
                filter.helperNote ??
                'Pick specific options, or leave blank to include all.'
              }
              values={choices}
              selected={selectedValues}
              onToggle={onToggle}
            />
          )}
        </div>
      )

    case 'single-select-dropdown':
    default:
      return <FallbackFilterRow filter={filter} />
  }
}

// ─── FilterRow ────────────────────────────────────────────────────────────────

export function FilterRow({
  filter,
  qualifiedDomainId,
  domainKey,
  selectedValues,
  onToggle,
  onSetSingle,
  onBlockingChange,
  onRetry,
  onCheckAgain,
  showDefaultBadge,
  ariaLabelOverride,
}: FilterRowProps) {
  const isSeasonFilter = filter.code === 'season'

  if (filter.isDiscoveryDriven) {
    return (
      <DiscoveryFilterRow
        domainKey={domainKey}
        filterCode={filter.code}
        label={filter.displayName}
        qualifiedDomainId={qualifiedDomainId}
        isSeasonFilter={isSeasonFilter}
        loadingVariant={
          filter.uiHint === 'grouped-checkbox-list' ? 'rows' : 'pills'
        }
        onRetry={onRetry}
        onCheckAgain={onCheckAgain}
        onBlockingChange={onBlockingChange}
        renderReady={choices =>
          renderHint(
            filter,
            choices,
            selectedValues,
            onToggle,
            onSetSingle,
            showDefaultBadge,
            ariaLabelOverride,
          )
        }
      />
    )
  }

  // Static — render directly. Filter staticChoices through displayChoices
  // whitelist if present (preserves the prior FE-render-only trim of
  // enrollment.status from 6+ SDK statuses to the 4 mockup-faithful ones).
  const allChoices = filter.staticChoices ?? []
  const filtered = filter.displayChoices
    ? allChoices.filter(c => filter.displayChoices!.includes(c.value))
    : allChoices
  const adapted: DiscoveredFilterValue[] = filtered.map(c => ({
    value: c.value,
    displayName: c.displayName,
    order: c.order,
  }))
  return renderHint(
    filter,
    adapted,
    selectedValues,
    onToggle,
    onSetSingle,
    showDefaultBadge,
    ariaLabelOverride,
  )
}
